import {DashboardFilterUISettings, DashboardFilterUISettingsInterface} from "./dashboard_filter_ui_settings";
import {DashboardFilter, DashboardFilterInterface} from "./dashboard_filter";
import {BehaviorSubject, combineLatest, combineLatestWith, distinctUntilChanged, firstValueFrom, map, Observable, of, shareReplay} from "rxjs";
import {ParentFilterSettings} from "./dashboard_filter_parent_filter_settings";
import {DashboardFilterSelectOption} from "./dashboard_filter_select_option";
import { MultiSelect, MultiSelectModule } from "primeng/multiselect";

export interface SelectFilterSettingsInterface {
  optionLabelKey?: string;
  optionValueKey?: string;
  isGrouped?: boolean;
  optionGroupLabelKey?: string;
  optionGroupItemsKey?: string;
}


export class SelectFilterSettings {
  optionLabelKey: string;
  optionValueKey: string;
  isGrouped: boolean;
  optionGroupLabelKey?: string;
  optionGroupItemsKey: string;

  constructor(args: SelectFilterSettingsInterface) {
    this.optionLabelKey = args.optionLabelKey || 'label';
    this.optionValueKey = args.optionValueKey || 'value';
    this.isGrouped = args.isGrouped || false;
    this.optionGroupLabelKey = args.optionGroupLabelKey;
    this.optionGroupItemsKey = args.optionGroupItemsKey || 'items';
  }
}


export interface SelectFilterUISettingsInterface extends DashboardFilterUISettingsInterface {
  placeholder?: string;
  emptyMessage?: string;
}


export class SelectFilterUISettings extends DashboardFilterUISettings {
  placeholder: string;
  emptyMessage: string;

  constructor(args: SelectFilterUISettingsInterface) {
    super(args);
    this.placeholder = args.placeholder || 'Auswählen...';
    this.emptyMessage = args.emptyMessage || 'Keine Optionen verfügbar';
  }
}


export interface SelectFilterInterface<V, S extends SelectFilterSettings, US extends SelectFilterUISettings, O, PFV> extends DashboardFilterInterface<V, SelectFilterUISettings, O, PFV> {
  options: O[];
  settings: S;
  uiSettings: US;
}


export abstract class SelectFilter<V, S extends SelectFilterSettings, US extends SelectFilterUISettings, O extends DashboardFilterSelectOption, PFV = any> extends DashboardFilter<V, US, O, PFV> {
  readonly settings: S;
  private readonly _options: O[];
  private _optionsFilteredByParentFilter: O[] = [];

  constructor(args: SelectFilterInterface<V, S, US, O, PFV>) {
    super(args);
    this._options = args.options;
    this.settings = args.settings;
  }

  private _options$!: Observable<O[]>;

  get options$(): Observable<O[]> {
    return this._options$;
  }

  get _flatBaseOptions(): any[] {
    return this._getFlatOptions(this._options);
  }

  get _flatOptionsFilteredByParentFilter(): any[] {
    const options = this._getFlatOptions(this._optionsFilteredByParentFilter);
    if (options.length == 0) {
      return this._flatBaseOptions;
    }
    return options;
  }

  _getFlatOptions(options: O[]): any[] {
    return this.settings.isGrouped ? options.flatMap(option => option[this.settings.optionGroupItemsKey]) : options;
  }

  _getOptionsObservable(options: O[], parentFilter?: DashboardFilter<PFV>, parentFilterSettings?: ParentFilterSettings<O, PFV>): Observable<O[]> {
    if (parentFilter && parentFilterSettings) {
      return parentFilter.value$.pipe(
        map(parentFilterValue => {
          const filteredOptions = parentFilterSettings.getFilteredChildOptions(parentFilterValue, options);
          this._optionsFilteredByParentFilter = filteredOptions;
          this.onParentFilterValueChanged(parentFilterValue, filteredOptions);
          return filteredOptions;
        })
      );
    } else {
      this._optionsFilteredByParentFilter = options;
      return of(options);
    }
  }

  onParentFilterValueChanged(parentFilterValue: PFV, filteredOptions: O[]) {

  }

  isValueValidInOptions(value: V, flatOptions: O[]): boolean {
    throw Error('Not implemented');
  }

  override initialize() {
    if (this.parentFilterSettings && !this.parentFilter || !this.parentFilterSettings && this.parentFilter) {
      throw Error(`${this.name}: Parent filter and its settings must be provided before initializing options`);
    }

    this._options$ = this._getOptionsObservable(this._options, this.parentFilter, this.parentFilterSettings);
    /*if (this.parentFilterSettings && this.parentFilterSettings.parentFilterName) {
      const filteredOptions = this.parentFilterSettings.getFilteredChildOptions(this.parentFilter?.value, options);
      this._optionsFilteredByParentFilter = filteredOptions;
    }*/
  }
}


export class SingleSelectFilterSettings extends SelectFilterSettings {
  constructor(args: SelectFilterSettingsInterface) {
    super(args);
  }
}


export interface SingleSelectFilterUISettingsInterface extends SelectFilterUISettingsInterface {
  autoDisplayFirst?: boolean;
  filter?: boolean;
  panellLessKeyboardNavigation?: boolean;
  showLabel?: boolean;
  autoSelectByIndex?: number
}

export class SingleSelectFilterUISettings extends SelectFilterUISettings {
  autoDisplayFirst?: boolean;
  filter?: boolean;
  panellLessKeyboardNavigation: boolean;
  autoSelectByIndex?: number

  constructor(args: SingleSelectFilterUISettingsInterface) {
    super(args);
    this.autoDisplayFirst = args.autoDisplayFirst ?? true;
    this.autoSelectByIndex = args.autoSelectByIndex;
    this.filter = args.filter ?? false;
    this.panellLessKeyboardNavigation = args.panellLessKeyboardNavigation ?? true;
    this.showLabel = args.showLabel ?? true;
  }
}


export class DashboardSingleSelectFilter<FV, PFV = any> extends SelectFilter<FV, SingleSelectFilterSettings, SingleSelectFilterUISettings, DashboardFilterSelectOption, PFV> {

  constructor(args: SelectFilterInterface<FV, SingleSelectFilterSettings, SingleSelectFilterUISettings, DashboardFilterSelectOption, PFV>) {

      if (args.initialValue == null && args.uiSettings.autoDisplayFirst && args.options.length > 0 && !args.parentFilterSettings?.parentFilterName) {
        args.initialValue = args.options[0][args.settings.optionValueKey] as FV;
      }

    super(args);
  }

  override isValueValid(value: FV): boolean {
    if (value == null) return true;

    const options = this._flatOptionsFilteredByParentFilter;
    return this.isValueValidInOptions(value, this._getFlatOptions(options));
  }

  override handleInvalidValue(value: FV) {
    const options = this._flatOptionsFilteredByParentFilter;
    if (!options.length) throw Error('Invalid Filter value and no options available');
    return this._getFlatOptions(options)[0][this.settings.optionValueKey] as FV;
  }

  override isValueValidInOptions(value: FV, flatOptions: DashboardFilterSelectOption[]): boolean {
    if (value == null) return true;
    return flatOptions.some(option => option[this.settings.optionValueKey] === value);
  }

  // override _parseNullValue(value: FV | null | undefined): FV {
  //   if (value == null) return undefined as unknown as FV;
  //   return value
  // }

  override initialize() {
    super.initialize();
    this.setInitialValue()
  }

  override onParentFilterValueChanged(parentFilterValue: PFV, filteredOptions: DashboardFilterSelectOption[]) {
    if (filteredOptions.length === 0) return;

    const options = this._getFlatOptions(filteredOptions);
    const valueValidInOptions = this.isValueValidInOptions(this.value, filteredOptions)

    if (this.uiSettings.autoSelectByIndex !== undefined) {
      // Clamp to the last option: parent-filtered option lists can be shorter than the requested index
      // (e.g. city states have fewer districts than there are comparison grids).
      const autoSelectIndex = Math.min(this.uiSettings.autoSelectByIndex, options.length - 1);
      const autoSelectedValue = options[autoSelectIndex][this.settings.optionValueKey];

      // Handle `null` explicitly because `isValueValidInOptions` considers it valid.
      // This ensures that an initial `null` value does not block the auto-selected default from being applied.
      if (this.value == null) {
        this.setValue(autoSelectedValue)
        return
      }

      // Apply autoSelectedIndex value only when value is invalid in the current option,
      // otherwise keep it as is
      if (!valueValidInOptions) {
        this.setValue(autoSelectedValue)
        return
      }
    }

    if ((this.value == null || !valueValidInOptions) && this.uiSettings.autoDisplayFirst) {
      const valueToSet = options[0][this.settings.optionValueKey];
      this.setValue(valueToSet);
    }
  }

  async setInitialValue() {
    // early return when a filter has parent
    // the parent filter will drive the value via onParentFilterValueChanged
    if (this.parentFilterSettings?.parentFilterName) return

    if (this._flatBaseOptions.length === 0) return

    const options = await firstValueFrom(this.options$)

    let valueToSet

    if (this.uiSettings.autoSelectByIndex !== undefined) {
      if (options.length === 0) return;
      const autoSelectIndex = Math.min(this.uiSettings.autoSelectByIndex, options.length - 1);
      valueToSet = options[autoSelectIndex][this.settings.optionValueKey]
      this.setValue(valueToSet);
      return;
    }

    if (this.value == null && this.uiSettings.autoDisplayFirst) {
      valueToSet = options[0][this.settings.optionValueKey];
      this.setValue(valueToSet);
      return;
    }

  }
}


export interface MultiSelectFilterUISettingsInterface extends SelectFilterUISettingsInterface {
  maxSelectedLabels?: number;
  selectedItemsLabel?: string;
  showToggleAll?: boolean;
  showToggleAllLabel?: boolean;
  toggleAllLabel?: string;
  showHeader?: boolean;
  showClear?: boolean;
  filter?: boolean;
  closeForClear?: boolean;
}

export class MultiSelectFilterUISettings extends SelectFilterUISettings {
  maxSelectedLabels: number;
  selectedItemsLabel: string;
  showToggleAll: boolean;
  showHeader: boolean;
  showClear: boolean;
  filter: boolean;
  closeForClear: boolean;
  showToggleAllLabel: boolean;
  toggleAllLabel: string;

  constructor(args: MultiSelectFilterUISettingsInterface) {
    super(args);
    this.maxSelectedLabels = args.maxSelectedLabels || 3;
    this.selectedItemsLabel = args.selectedItemsLabel || '{0} ausgewählt';
    this.showToggleAll = args.showToggleAll ?? true;
    this.showHeader = args.showHeader ?? true;
    this.showClear = args.showClear || false;
    this.filter = args.filter || false;
    this.closeForClear = args.closeForClear || false;
    this.showToggleAllLabel = args.showToggleAllLabel ?? true;
    this.toggleAllLabel = args.toggleAllLabel || 'Alle';
  }
}


export interface MultiSelectFilterSettingsInterface<FV, O, PFV = any> extends SelectFilterSettingsInterface {
  allInitiallySelected?: boolean;
  allSelectedOnParentChange?: boolean;
  allInitiallySelectedExceptValue?: FV[];
  preserveSingleSelectionOnParentChange?: boolean;
  fallbackSingleSelectionToFirstOption?: boolean;
  fallbackSingleSelectionOnParentChange?: (parentFilterValue: PFV, flatOptions: DashboardFilterSelectOption[]) => FV | undefined;
  initialValues?: O[];
}

export class MultiSelectFilterSettings<FV, O, PFV = any> extends SelectFilterSettings {
  allInitiallySelected: boolean;
  allSelectedOnParentChange: boolean;
  allInitiallySelectedExceptValue: FV[];
  preserveSingleSelectionOnParentChange: boolean;
  fallbackSingleSelectionToFirstOption: boolean;
  fallbackSingleSelectionOnParentChange?: (parentFilterValue: PFV, flatOptions: DashboardFilterSelectOption[]) => FV | undefined;

  constructor(args: MultiSelectFilterSettingsInterface<FV, O, PFV>) {
    super(args);
    this.allInitiallySelected = args.allInitiallySelected || false;
    this.allSelectedOnParentChange = args.allSelectedOnParentChange || false;
    this.allInitiallySelectedExceptValue = args.allInitiallySelectedExceptValue || [];
    this.preserveSingleSelectionOnParentChange = args.preserveSingleSelectionOnParentChange ?? false;
    this.fallbackSingleSelectionToFirstOption = args.fallbackSingleSelectionToFirstOption ?? false;
    this.fallbackSingleSelectionOnParentChange = args.fallbackSingleSelectionOnParentChange;
  }
}


export class DashboardMultiSelectFilter<FV, FS extends MultiSelectFilterSettings<FV, DashboardFilterSelectOption> = MultiSelectFilterSettings<FV, DashboardFilterSelectOption>, FUIS extends MultiSelectFilterUISettings = MultiSelectFilterUISettings, PFV = any> extends SelectFilter<FV[], FS, FUIS, DashboardFilterSelectOption, PFV> {

  _filteredValue$!: Observable<FV[]>;
  _previouslySelectedOptionsNotAvailableCurrently: FV[] = [];
  _lastEffectiveValue: FV[] = [];
  _isAllSelected$: Observable<boolean> = new Observable()

  override get value$(): Observable<FV[]> {
    return this._filteredValue$;
  }

  override setValue(value: FV[] | undefined | null) {
    const tmpValueWithPreviouslySelectedOptionsNotAvailableCurrently = value?.concat(this._previouslySelectedOptionsNotAvailableCurrently);
    super.setValue(tmpValueWithPreviouslySelectedOptionsNotAvailableCurrently);
  }

  get isAllSelected$() {
    return this._isAllSelected$;
  }

  /*override filterValidValue(value: FV[]): FV[] {
    const options = await lastValueFrom(this.options$);
    return value.filter(v => this._isSingleValueValid(v, this._flatBaseOptions));
  }*/

  _getFilteredValueObservable(): Observable<FV[]> {
    return combineLatest([this._value$.pipe(distinctUntilChanged(), map((v) => {
      return v;
    })), this.options$.pipe(distinctUntilChanged(), map((v) => {
      return v;
    }))]).pipe(
      map(([value, options]) => {
        const flatOptions = this._getFlatOptions(options);
        const validValues = value.filter(v => this._isSingleValueValid(v, flatOptions));
        // remove duplicates
        const validValuesNoDuplicates = validValues.filter((v, i) => validValues.indexOf(v) === i);
        this._previouslySelectedOptionsNotAvailableCurrently = value.filter(v => validValuesNoDuplicates.indexOf(v) === -1);
        this._lastEffectiveValue = validValuesNoDuplicates;
        return validValuesNoDuplicates;
      }),
      shareReplay(1)
    );
  }

  _getIsAllSelectedObservable(): Observable<boolean> {
    return combineLatest([this._filteredValue$, this.options$]).pipe(
      map(([filteredValue, options]) => {
        const flatOptions = this._getFlatOptions(options);
        return filteredValue.length === flatOptions.length;
      }),
      shareReplay(1)
    );
  }

  override isValueValid(value: FV[]): boolean {
    return this.isValueValidInOptions(value, this._flatBaseOptions);
  }

  override isValueValidInOptions(value: FV[], flatOptions: DashboardFilterSelectOption[]): boolean {
    return value.every(v => this._isSingleValueValid(v, flatOptions));
  }

  _isSingleValueValid(value: FV, options: any[]): boolean {
    // Option values are strings; restored / peer ids may be numbers.
    return options.some(
      (option) => `${option[this.settings.optionValueKey]}` === `${value}`,
    );
  }

  override handleInvalidValue(value: FV[]): FV[] {
    // Keep still-valid entries instead of throwing (slice restore can mix types).
    return (value ?? []).filter((v) =>
      this._isSingleValueValid(v, this._flatBaseOptions),
    );
  }

  override _parseNullValue(value: null | undefined | FV[]): FV[] {
    if (value == null) return [];
    return value;
  }

  protected _resolveInvalidSingleSelectionFallback(parentFilterValue: PFV, flatOptions: DashboardFilterSelectOption[]): FV | undefined {
    const customFallback = this.settings.fallbackSingleSelectionOnParentChange?.(parentFilterValue, flatOptions);
    if (customFallback != null) {
      return customFallback;
    }

    if (this.settings.fallbackSingleSelectionToFirstOption && flatOptions.length > 0) {
      return flatOptions[0][this.settings.optionValueKey] as FV;
    }

    return undefined;
  }

  override onParentFilterValueChanged(parentFilterValue: PFV, filteredOptions: DashboardFilterSelectOption[]) {
    if (this.settings.preserveSingleSelectionOnParentChange && this._lastEffectiveValue.length === 1) {
      const [onlySelectedValue] = this._lastEffectiveValue;
      this._previouslySelectedOptionsNotAvailableCurrently = [];
      const flatOptions = this._getFlatOptions(filteredOptions);
      let valueToKeep: FV[];
      if (this._isSingleValueValid(onlySelectedValue, flatOptions)) {
        valueToKeep = [onlySelectedValue];
      } else {
        const fallbackValue = this._resolveInvalidSingleSelectionFallback(parentFilterValue, flatOptions);
        valueToKeep = fallbackValue != null ? [fallbackValue] : [];
      }
      this.setValueNoChecks(valueToKeep);
      return;
    }

    if (this.settings.allSelectedOnParentChange && filteredOptions.length > 0) {
      this.setValue(this.getAllInclusiveValues(filteredOptions));
    }
  }

  override initialize() {
    if (this.settings.allInitiallySelected) {
      this.setValue(this.getAllInclusiveValues(this._flatBaseOptions));
    }
    super.initialize();
    this._filteredValue$ = this._getFilteredValueObservable();
    this._isAllSelected$ = this._getIsAllSelectedObservable();
  }

  multiSelectElement!: MultiSelect;
  multiSelectFilterInput: HTMLInputElement | null = null;

  private getAllInclusiveValues(options: DashboardFilterSelectOption[]): FV[] | null | undefined {
    return options
      .filter(option => !this.settings.allInitiallySelectedExceptValue.some(
        exceptValue => option[this.settings.optionValueKey] === exceptValue
      ))
      .map(option => option[this.settings.optionValueKey]) as FV[];
  }

  onPanelShow(multiSelectElement: any) {
    this.multiSelectElement = multiSelectElement;
    this.multiSelectFilterInput = multiSelectElement.filterInputChild?.nativeElement;

    if (this.uiSettings.showToggleAll && this.uiSettings.showToggleAllLabel) {
      this.addToggleAllLabel();
    }

    if (this.uiSettings.closeForClear) {
      // Disable original close button so that it doesn't close the panel
      const multiSelectCloseIcon = document.querySelector('button.p-multiselect-close')!
      multiSelectCloseIcon.setAttribute('disabled', 'true')
    }
  }

  addToggleAllLabel() {
    const toggleAllCheckbox = document.querySelector('.p-multiselect-header .p-checkbox')

    // Create toggleAllCheckboxLabel
    const toggleAllCheckboxLabel = document.createElement('label')
    const labelTextNode = document.createTextNode(this.uiSettings.toggleAllLabel!)
    toggleAllCheckboxLabel.appendChild(labelTextNode)

    // insert toggleAllCheckboxLabel after toggleAllCheckbox
    toggleAllCheckbox!.insertAdjacentHTML('afterend', toggleAllCheckboxLabel.outerHTML)
  }

  clearValueOnCloseBtnClick() {
    if (this.multiSelectFilterInput && this.multiSelectFilterInput.value !== '') {
      this.multiSelectElement.resetFilter()
    } else {
      this.setValue([])
    }
  }
}
